import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Brain, 
  MessageCircle, 
  Mic, 
  FileText, 
  GraduationCap, 
  Activity,
  Clock,
  DollarSign
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Specialty {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
  price: number;
}

const Services = () => {
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSpecialties();
  }, []);

  const fetchSpecialties = async () => {
    try {
      const { data, error } = await supabase
        .from('specialties_2025_11_26_20_41')
        .select('*')
        .order('name');

      if (error) throw error;
      setSpecialties(data || []);
    } catch (error) {
      console.error('Erro ao buscar especialidades:', error);
    } finally {
      setLoading(false);
    }
  };

  const getServiceIcon = (serviceName: string) => {
    const name = serviceName.toLowerCase();
    if (name.includes('psicologia')) return Brain;
    if (name.includes('psicanálise')) return MessageCircle;
    if (name.includes('fonoaudiologia')) return Mic;
    if (name.includes('neuropsicológica') || name.includes('psicopedagógica')) return Brain;
    if (name.includes('laudos')) return FileText;
    if (name.includes('reforço') || name.includes('escolar')) return GraduationCap;
    if (name.includes('atividades') || name.includes('idosos')) return Activity;
    return Brain;
  };

  const scrollToAppointment = () => {
    const element = document.querySelector('#appointment');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-12 bg-gray-200 rounded w-96 mx-auto mb-8"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header da seção */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            Nossos Serviços
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Especialidades que
            <span className="block text-primary">cuidam de você</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Oferecemos uma ampla gama de serviços especializados para atender 
            todas as suas necessidades de saúde mental e bem-estar.
          </p>
        </div>

        {/* Grid de serviços */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {specialties.map((specialty) => {
            const IconComponent = getServiceIcon(specialty.name);
            
            return (
              <Card 
                key={specialty.id} 
                className="hover:shadow-medium transition-smooth group cursor-pointer"
              >
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-smooth">
                    <IconComponent className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-gray-900 group-hover:text-primary transition-smooth">
                    {specialty.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {specialty.description}
                  </p>
                  
                  <div className="flex justify-between items-center mb-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{specialty.duration_minutes} min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-4 w-4" />
                      <span>R$ {specialty.price?.toFixed(2)}</span>
                    </div>
                  </div>

                  <Button 
                    onClick={scrollToAppointment}
                    className="w-full gradient-primary text-white hover:opacity-90 transition-smooth"
                  >
                    Agendar Consulta
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Seção de diferenciais */}
        <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Por que escolher a Clínica Aymee?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="h-10 w-10 text-primary" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">
                Abordagem Multidisciplinar
              </h4>
              <p className="text-gray-600">
                Diferentes especialidades trabalhando em conjunto para 
                oferecer o melhor tratamento possível.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-10 w-10 text-secondary" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">
                Laudos Especializados
              </h4>
              <p className="text-gray-600">
                Emissão de laudos médicos para cirurgias bariátricas, 
                estéticas e vasectomia com agilidade e precisão.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Activity className="h-10 w-10 text-accent" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">
                Cuidado Integral
              </h4>
              <p className="text-gray-600">
                Do reforço escolar às atividades para idosos, 
                cuidamos de todas as fases da vida.
              </p>
            </div>
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Pronto para cuidar da sua saúde mental?
          </h3>
          <p className="text-gray-600 mb-6">
            Agende sua consulta e dê o primeiro passo para uma vida mais equilibrada.
          </p>
          <Button 
            onClick={scrollToAppointment}
            size="lg"
            className="gradient-primary text-white hover:opacity-90 transition-smooth px-8 py-4"
          >
            Agendar Agora
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;